import { useState } from "react";
import { CheckCircle2, Download, ExternalLink, ClipboardList, GitCommit, Clock } from "lucide-react";
import { downloadFileFromUrl, getBlobUrlFromFileUrl, isExternalDocumentUrl } from "../utils/downloadUtil";
import { formatDateTime } from "../utils/dateUtils";
import { SurfaceCard, StatusBadge, StatTile, Button, Modal } from "../../shared/ui";
import DynamicFileViewer from "../../components/FileViewers/DynamicFileViewer";

const SUMMARY_MAX_CHARS = 280;

const ResultDisplay = ({ result, compact = false }) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [isViewing, setIsViewing] = useState(false);
  const [summaryExpanded, setSummaryExpanded] = useState(false);
  
  const [viewerModalOpen, setViewerModalOpen] = useState(false);
  const [viewerBlobUrl, setViewerBlobUrl] = useState(null);
  const [viewerFilename, setViewerFilename] = useState("");

  if (!result) return null;

  const rawSummary = result.executive_summary || "";
  const isLong = rawSummary.length > SUMMARY_MAX_CHARS;
  const displaySummary = isLong && !summaryExpanded
    ? rawSummary.slice(0, SUMMARY_MAX_CHARS).trimEnd() + "…"
    : rawSummary || "No executive summary available.";

  const handleDownloadClick = async (e) => {
    e.preventDefault();
    if (!result.published_url) return;
    if (isExternalDocumentUrl(result.published_url)) {
      window.open(result.published_url, "_blank", "noopener,noreferrer");
      return;
    }
    setIsDownloading(true);
    try {
      await downloadFileFromUrl(result.published_url);
    } catch {
      // Do NOT fall back to a raw window.open(published_url) here — that
      // bypasses the axios Authorization header the same way the old
      // "View Document" plain <a href> did, hitting the exact same
      // permission-denied response instead of actually recovering.
    } finally {
      setIsDownloading(false);
    }
  };

  const handleViewClick = async (e) => {
    e.preventDefault();
    if (!result.published_url) return;
    if (isExternalDocumentUrl(result.published_url)) {
      window.open(result.published_url, "_blank", "noopener,noreferrer");
      return;
    }
    setIsViewing(true);
    try {
      const { blobUrl, filename } = await getBlobUrlFromFileUrl(result.published_url);
      setViewerBlobUrl(blobUrl);
      setViewerFilename(filename);
      setViewerModalOpen(true);
    } catch (err) {
      console.error("Failed to open document:", err);
    } finally {
      setIsViewing(false);
    }
  };

  const closeViewerModal = () => {
    setViewerModalOpen(false);
    if (viewerBlobUrl) {
      window.URL.revokeObjectURL(viewerBlobUrl);
      setViewerBlobUrl(null);
    }
  };

  return (
    <SurfaceCard padding={compact ? "p-0" : "p-6"} className={compact ? "border-0 shadow-none" : "mt-6"}>
      <div className="flex flex-col gap-5">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 bg-status-good/10 rounded-2xl flex items-center justify-center shrink-0">
            <CheckCircle2 className="text-status-good" size={32} />
          </div>
          <div className="min-w-0">
            <h3 className="text-xl font-bold text-ink m-0">Generation Successful</h3>
            <div className="mt-1.5">
              <StatusBadge map="adai" status={result.status || "completed"} size="sm" />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <StatTile icon={ClipboardList} label="Work Items" value={result.total_work_items ?? 0} />
          <StatTile icon={GitCommit} label="Diffs" value={result.total_diffs ?? 0} />
          <StatTile icon={Clock} label="Generated" value={formatDateTime(result.updated_at) || "—"} />
        </div>

        <div className="bg-gray-50 p-5 rounded-card border border-line">
          <span className="text-[11px] uppercase font-bold text-muted block mb-3">
            Executive Summary
          </span>
          <p className="text-sm text-ink leading-relaxed m-0">{displaySummary}</p>
          {isLong && (
            <button
              type="button"
              onClick={() => setSummaryExpanded((v) => !v)}
              className="mt-3 text-xs font-semibold text-brand-700 hover:text-brand-800 border-0 bg-transparent cursor-pointer p-0"
            >
              {summaryExpanded ? "Show less" : "Read more"}
            </button>
          )}
        </div>

        <div className="flex flex-col sm:flex-row justify-end gap-3">
          <Button
            variant="secondary"
            onClick={handleViewClick}
            disabled={isViewing || !result.published_url}
            loading={isViewing}
          >
            <ExternalLink size={16} />
            {isViewing ? "Opening…" : "View Document"}
          </Button>
          <Button
            variant="primary"
            onClick={handleDownloadClick}
            disabled={isDownloading || !result.published_url}
            loading={isDownloading}
          >
            <Download size={16} />
            {isDownloading ? "Downloading…" : "Download File"}
          </Button>
        </div>
      </div>
      <Modal
        open={viewerModalOpen}
        onClose={closeViewerModal}
        title={viewerFilename || "Document Preview"}
        size="lg"
      >
        <DynamicFileViewer
          fileUrl={viewerBlobUrl}
          filename={viewerFilename || result.published_url}
        />
      </Modal>
    </SurfaceCard>
  );
};

export default ResultDisplay;
